/* src/components/Skeleton.jsx
 * Shared animated skeleton primitives for console loading states.
 */

import React from 'react';

export function Skeleton({
  width,
  height,
  radius = '4px',
  className = '',
  style,
}) {
  return (
    <div
      className={`skeleton ${className}`.trim()}
      style={{ width, height, borderRadius: radius, ...style }}
      aria-hidden="true"
    />
  );
}

export function SkeletonText({ lines = 3, gap = '0.5rem', className }) {
  return (
    <div
      className={className}
      style={{ display: 'flex', flexDirection: 'column', gap }}
      aria-hidden="true"
    >
      {Array.from({ length: lines }, (_, i) => (
        <Skeleton
          key={i}
          width={i === lines - 1 ? '70%' : '100%'}
          height="1.25rem"
        />
      ))}
    </div>
  );
}

export function SkeletonRow({ children, gap = '8px', className, style }) {
  return (
    <div
      className={className}
      style={{ display: 'flex', alignItems: 'center', gap, ...style }}
      aria-hidden="true"
    >
      {children}
    </div>
  );
}

export function InboxListSkeleton({ rows = 6 }) {
  return (
    <div aria-busy="true" aria-label="Loading leads">
      {Array.from({ length: rows }, (_, i) => (
        <div
          key={i}
          className="inbox-row"
          style={{ cursor: 'default', pointerEvents: 'none' }}
        >
          <SkeletonRow style={{ justifyContent: 'space-between' }}>
            <Skeleton width="58%" height="14px" />
            <Skeleton width="40px" height="10px" radius="2px" />
          </SkeletonRow>
          <Skeleton width="88%" height="12px" style={{ marginTop: '3px' }} />
          <SkeletonRow style={{ marginTop: '8px', gap: '6px' }}>
            <Skeleton width="72px" height="16px" radius="4px" />
            <Skeleton width="52px" height="16px" radius="4px" />
          </SkeletonRow>
        </div>
      ))}
    </div>
  );
}

export function TodayPageSkeleton({ isMobile, showHeader = false, embedded = false }) {
  const content = (
    <>
      {showHeader && (
        <div className="today-header" style={{ marginBottom: '1.5rem' }}>
          <Skeleton width="120px" height="32px" radius="6px" />
        </div>
      )}

      <div
        className="today-stat-strip"
        style={{
          gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(4, minmax(0, 1fr))',
        }}
      >
        {Array.from({ length: 4 }, (_, i) => (
          <div key={i} className="today-stat-card">
            <Skeleton width="72px" height="10px" radius="2px" />
            <Skeleton width="48px" height="30px" radius="6px" style={{ marginTop: '8px' }} />
          </div>
        ))}
      </div>

      <div
        className="today-two-col"
        style={{
          gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 1.1fr) minmax(0, 1fr)',
        }}
      >
        {Array.from({ length: 2 }, (_, panelIdx) => (
          <div key={panelIdx} className="today-panel">
            <div className="today-panel-header">
              <Skeleton width="100px" height="15px" radius="4px" />
            </div>
            <div className="today-panel-body">
              {Array.from({ length: 3 }, (_, rowIdx) => (
                <div key={rowIdx} className="today-queue-row">
                  <SkeletonRow style={{ justifyContent: 'space-between' }}>
                    <Skeleton width="55%" height="14px" />
                    <Skeleton width="44px" height="10px" radius="2px" />
                  </SkeletonRow>
                  <Skeleton width="75%" height="12px" style={{ marginTop: '6px' }} />
                  <SkeletonRow style={{ marginTop: '8px', gap: '6px' }}>
                    <Skeleton width="64px" height="16px" radius="4px" />
                    <Skeleton width="48px" height="16px" radius="4px" />
                  </SkeletonRow>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="today-panel" style={{ marginTop: '0' }}>
        <div className="today-panel-header">
          <Skeleton width="80px" height="15px" radius="4px" />
        </div>
        <div className="today-panel-body" style={{ padding: '14px 20px' }}>
          <SkeletonRow gap="12px" style={{ flexWrap: 'wrap' }}>
            {Array.from({ length: 5 }, (_, i) => (
              <Skeleton key={i} width="88px" height="52px" radius="8px" />
            ))}
          </SkeletonRow>
        </div>
      </div>
    </>
  );

  if (embedded) {
    return (
      <div aria-busy="true" aria-label="Loading today">
        {content}
      </div>
    );
  }

  return (
    <div
      className="today-page"
      style={{ padding: isMobile ? '1rem 1rem 2rem' : '24px 32px 48px' }}
      aria-busy="true"
      aria-label="Loading today"
    >
      {content}
    </div>
  );
}

export function PipelineBoardSkeleton({ columns = 5 }) {
  return (
    <div className="pipeline-board" aria-busy="true" aria-label="Loading pipeline">
      {Array.from({ length: columns }, (_, colIdx) => (
        <div key={colIdx} className="pipeline-column">
          <div className="pipeline-column-header">
            <Skeleton width="64px" height="10px" radius="2px" />
            <Skeleton width="28px" height="20px" radius="4px" style={{ marginTop: '6px' }} />
          </div>
          <div className="pipeline-column-body">
            {Array.from({ length: colIdx % 2 === 0 ? 2 : 1 }, (_, cardIdx) => (
              <div
                key={cardIdx}
                className="pipeline-card"
                style={{ cursor: 'default', pointerEvents: 'none' }}
              >
                <SkeletonRow style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Skeleton width="75%" height="14px" />
                    <Skeleton width="60%" height="10px" radius="2px" style={{ marginTop: '4px' }} />
                  </div>
                </SkeletonRow>
                <Skeleton width="48px" height="12px" radius="2px" style={{ marginTop: '8px' }} />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export function KbTableSkeleton({ rows = 5 }) {
  return (
    <div className="fb-table-wrap" aria-busy="true" aria-label="Loading knowledge bases">
      {Array.from({ length: rows }, (_, i) => (
        <div
          key={i}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            padding: '12px 16px',
            borderBottom: i < rows - 1 ? '1px solid var(--hairline-1)' : 'none',
            minHeight: '52px',
          }}
        >
          <div style={{ flex: '0 0 42%', minWidth: 0 }}>
            <Skeleton width="68%" height="14px" />
            <Skeleton width="48%" height="12px" style={{ marginTop: '4px' }} />
          </div>
          <div style={{ flex: '0 0 30%' }}>
            <SkeletonRow gap="6px">
              <Skeleton width="72px" height="16px" radius="4px" />
              <Skeleton width="52px" height="16px" radius="4px" />
            </SkeletonRow>
          </div>
          <div style={{ flex: '0 0 12%' }}>
            <Skeleton width="72px" height="11px" radius="2px" />
          </div>
          <div style={{ flex: '0 0 170px', display: 'flex', gap: '8px' }}>
            <Skeleton width="48px" height="28px" radius="6px" />
            <Skeleton width="40px" height="28px" radius="6px" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function FormsListSkeleton({ cards = 3 }) {
  return (
    <div className="forms-list-grid" aria-busy="true" aria-label="Loading forms">
      {Array.from({ length: cards }, (_, i) => (
        <div key={i} className="today-panel forms-list-card">
          <div className="forms-list-card-body">
            <Skeleton width="55%" height="1.05rem" />
            <Skeleton width="42%" height="11px" style={{ marginTop: '0.5rem' }} />
            <Skeleton width="28%" height="11px" style={{ marginTop: '0.15rem' }} />
            <SkeletonRow style={{ marginTop: '1rem', flexWrap: 'wrap' }}>
              <Skeleton width="88px" height="32px" radius="6px" />
              <Skeleton width="48px" height="32px" radius="6px" />
              <Skeleton width="100px" height="32px" radius="6px" />
            </SkeletonRow>
          </div>
        </div>
      ))}
    </div>
  );
}

export function ProofListSkeleton({ rows = 4 }) {
  return (
    <div className="proof-log-body" aria-busy="true" aria-label="Loading conversions">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="proof-row">
          <SkeletonRow style={{ justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
            <div style={{ minWidth: 0 }}>
              <Skeleton width="120px" height="11px" radius="2px" />
              <Skeleton width="140px" height="14px" style={{ marginTop: '4px' }} />
            </div>
            <Skeleton width="80px" height="11px" radius="2px" />
          </SkeletonRow>
          <SkeletonRow gap="6px">
            <Skeleton width="56px" height="16px" radius="4px" />
            <Skeleton width="48px" height="16px" radius="4px" />
          </SkeletonRow>
        </div>
      ))}
    </div>
  );
}

export function QuarantineListSkeleton({ rows = 4 }) {
  return (
    <div style={{ padding: '0 20px' }} aria-busy="true" aria-label="Loading submissions">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="quarantine-row">
          <SkeletonRow style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div style={{ minWidth: 0 }}>
              <Skeleton width="100px" height="11px" radius="2px" />
              <Skeleton width="130px" height="14px" style={{ marginTop: '4px' }} />
            </div>
            <SkeletonRow gap="6px">
              <Skeleton width="56px" height="16px" radius="4px" />
              <Skeleton width="64px" height="16px" radius="4px" />
            </SkeletonRow>
          </SkeletonRow>
        </div>
      ))}
    </div>
  );
}

export function TeamMembersSkeleton({ rows = 3 }) {
  return (
    <div className="fb-table-wrap" style={{ border: 'none', borderRadius: 0 }} aria-busy="true" aria-label="Loading team members">
      {Array.from({ length: rows }, (_, i) => (
        <div
          key={i}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            padding: '12px 16px',
            borderBottom: i < rows - 1 ? '1px solid var(--hairline-1)' : 'none',
            minHeight: '64px',
          }}
        >
          <div style={{ flex: '0 0 38%', minWidth: 0 }}>
            <Skeleton width="62%" height="14px" />
            <Skeleton width="48%" height="12px" style={{ marginTop: '4px' }} />
            <Skeleton width="72%" height="11px" radius="2px" style={{ marginTop: '4px' }} />
          </div>
          <div style={{ flex: '0 0 12%' }}>
            <Skeleton width="72px" height="16px" radius="4px" />
          </div>
          <div style={{ flex: '0 0 10%', display: 'flex', justifyContent: 'center' }}>
            <Skeleton width="16px" height="16px" radius="3px" />
          </div>
          <div style={{ flex: '0 0 10%', display: 'flex', justifyContent: 'center' }}>
            <Skeleton width="16px" height="16px" radius="3px" />
          </div>
          <div style={{ flex: '0 0 18%', display: 'flex', justifyContent: 'flex-end' }}>
            <Skeleton width="72px" height="28px" radius="6px" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function BillingPlanSkeleton() {
  return (
    <div className="today-panel billing-section-panel" aria-busy="true" aria-label="Loading subscription status">
      <div className="today-panel-header billing-panel-toolbar">
        <Skeleton width="100px" height="15px" radius="4px" />
        <Skeleton width="180px" height="32px" radius="6px" />
      </div>
      <div className="today-panel-body">
        <div className="fb-callout success billing-status-block" style={{ pointerEvents: 'none' }}>
          <Skeleton width="140px" height="18px" radius="4px" style={{ marginBottom: '0.75rem' }} />
          <Skeleton width="48px" height="11px" radius="2px" style={{ marginBottom: '4px' }} />
          <Skeleton width="120px" height="22px" radius="4px" style={{ marginBottom: '0.75rem' }} />
          <Skeleton width="180px" height="14px" style={{ marginBottom: '0.75rem' }} />
          <Skeleton width="120px" height="24px" radius="999px" />
        </div>
      </div>
    </div>
  );
}

export function AdminUsersTableSkeleton({ rows = 6 }) {
  return (
    <div
      style={{
        background: 'white',
        borderRadius: '8px',
        padding: '2rem',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      }}
      aria-busy="true"
      aria-label="Loading users"
    >
      <Skeleton width="140px" height="24px" radius="4px" style={{ marginBottom: '1.5rem' }} />
      <div style={{ overflowX: 'auto' }}>
        <SkeletonRow gap="12px" style={{ padding: '0.75rem 1rem', borderBottom: '2px solid #eee' }}>
          {['22%', '10%', '12%', '8%', '10%', '12%', '12%', '8%', '6%'].map((w, i) => (
            <Skeleton key={i} width={w} height="12px" radius="2px" />
          ))}
        </SkeletonRow>
        {Array.from({ length: rows }, (_, i) => (
          <SkeletonRow
            key={i}
            gap="12px"
            style={{ padding: '1rem', borderBottom: i < rows - 1 ? '1px solid #eee' : 'none', alignItems: 'center' }}
          >
            <Skeleton width="22%" height="14px" />
            <Skeleton width="72px" height="22px" radius="12px" />
            <Skeleton width="12%" height="32px" radius="4px" />
            <Skeleton width="48px" height="28px" radius="4px" />
            <Skeleton width="10%" height="12px" radius="2px" />
            <Skeleton width="12%" height="12px" radius="2px" />
            <Skeleton width="12%" height="12px" radius="2px" />
            <Skeleton width="64px" height="28px" radius="4px" />
            <Skeleton width="16px" height="16px" radius="3px" />
          </SkeletonRow>
        ))}
      </div>
    </div>
  );
}

export function OnboardingChecklistSkeleton({ rows = 5 }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }} aria-busy="true" aria-label="Loading setup checklist">
      {Array.from({ length: rows }, (_, i) => (
        <SkeletonRow
          key={i}
          gap="12px"
          style={{ alignItems: 'flex-start', padding: '0.65rem 0.75rem' }}
        >
          <Skeleton width="16px" height="16px" radius="50%" style={{ flexShrink: 0, marginTop: '2px' }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <Skeleton width="75%" height="14px" />
            {i % 2 === 1 && (
              <Skeleton width="55%" height="11px" radius="2px" style={{ marginTop: '6px' }} />
            )}
          </div>
        </SkeletonRow>
      ))}
    </div>
  );
}
